import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format, isValid } from 'date-fns';
import {
  X, Loader, Plus, Trash2, Edit3, Save, Ban, Archive, Eye, EyeOff,
  Download, Upload, Link2, MessageSquare, Paperclip, CheckSquare,
  History, AlertTriangle, ChevronDown, ChevronRight,
  GripVertical, Users, Tag, Activity, Settings, Search, UserPlus, ExternalLink,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { kanbanApiService } from '../../services/kanbanApi';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { buildTeamApiService } from '../../services/buildTeamApi';
import { incidentsApiService } from '../../services/incidentsApi';
import { calendarApiService } from '../../services/calendarApi';
import { modpackNotesApiService } from '../../services/modpackNotesApi';
import { appealsApiService } from '../../services/appealsApi';
import { notesApiService } from '../../services/notesApi';
import { usernameCacheService, useUsernameCacheVersion } from '../../services/usernameCache';
import { elasticsearchService } from '../../services/elasticsearchService';
import { useDebounce } from '../../hooks/useDebounce';
import { PlayerProfile } from '../PlayerProfile';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { ConfirmationModal } from '../ConfirmationModal';
import { TicketIdLink } from '../TicketIdLink';
import { SkeletonLoader } from '../SkeletonLoader';
import { EmptyState } from '../ui/EmptyState';
import { KanbanCardTypeBadge } from './KanbanCardTypeBadge';
import { KanbanPriorityBadge } from './KanbanPriorityBadge';
import { KanbanClassOfServiceIndicator } from './KanbanClassOfServiceIndicator';
import { AddCardLinkModal } from './AddCardLinkModal';
import { CardIncidentsSection } from './CardIncidentsSection';
import { CardEventsSection } from './CardEventsSection';
import type { Incident } from '../../types/incident';
import type { EventItem } from '../../types/eventTeam';
import { cn } from '../../utils/cn';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useAuth } from '../../contexts/AuthContext';
import {
  KANBAN_CARD_TYPES,
  KANBAN_CLASSES_OF_SERVICE,
  LINK_TYPE_CONFIG,
  type KanbanCardLinkType,
  type KanbanCardFull,
  type KanbanCardHistory,
  type KanbanActivityLogEntry,
  type KanbanFullBoard,
  type KanbanCardType,
  type KanbanClassOfService,
} from '../../types/kanban';

interface KanbanCardDetailPanelProps {
  cardId: string;
  boardId: string;
  boardData: KanbanFullBoard | null;
  onClose: () => void;
  onUpdated: () => void;
  /** Keeps board card chips in sync with full card payloads (e.g. after GET /cards/:id). */
  onCardHydrated?: (full: KanbanCardFull) => void;
  canWrite: boolean;
  currentUserUuid?: string;
  /** Incremented when the board WebSocket signals `refetch_card_detail` for this card. */
  wsRefetchNonce?: number;
  /**
   * Incremented when the board WebSocket signals `card.incident_linked` /
   * `card.incident_unlinked` for this card so the linked-incidents section refetches.
   */
  cardIncidentsRefreshNonce?: number;
  /** Incremented when the board WebSocket signals `card.event_linked` for this card. */
  cardEventsRefreshNonce?: number;
}

type ActiveTab = 'details' | 'activity' | 'history';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function parseDate(value: string | number | Date | null | undefined): Date | null {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return isValid(d) ? d : null;
}

function safeFormat(value: string | number | Date | null | undefined, fmt: string, fallback = '—'): string {
  const d = parseDate(value);
  return d ? format(d, fmt) : fallback;
}

function isImageContentType(ct: string | undefined | null): boolean {
  if (!ct) return false;
  return ct.startsWith('image/');
}

function safeFormatDistanceToNow(
  value: string | number | Date | null | undefined,
  opts: { addSuffix: boolean },
  fallback = '—',
): string {
  const d = parseDate(value);
  return d ? formatDistanceToNow(d, opts) : fallback;
}

interface PlayerEsHit {
  _id: string;
  _source: { uuid: string; lastKnownName: string; nickname: string; lastKnownIp: string };
}

/** GET /cards/:id may omit relation arrays; the panel assumes arrays everywhere. */
function normalizeKanbanCardFull(c: KanbanCardFull): KanbanCardFull {
  const checklists = (Array.isArray(c.checklists) ? c.checklists : []).map((cl) => ({
    ...cl,
    items: Array.isArray(cl.items) ? cl.items : [],
  }));
  return {
    ...c,
    labels: Array.isArray(c.labels) ? c.labels : [],
    assignees: Array.isArray(c.assignees) ? c.assignees : [],
    checklists,
    comments: Array.isArray(c.comments) ? c.comments : [],
    attachments: Array.isArray(c.attachments) ? c.attachments : [],
    watchers: Array.isArray(c.watchers) ? c.watchers : [],
    links: Array.isArray(c.links) ? c.links : [],
  };
}

/**
 * Some PATCH/GET responses return an empty relation array while the card still has data.
 * Preserve non-empty local sections when the server sends an empty list (same idea as board summary counts).
 */
function mergeKanbanCardFull(prev: KanbanCardFull | null, incoming: KanbanCardFull): KanbanCardFull {
  const next = normalizeKanbanCardFull(incoming);
  if (!prev) return next;
  const keepIfEmpty = <K extends keyof KanbanCardFull>(key: K): KanbanCardFull[K] => {
    const n = next[key];
    const p = prev[key];
    if (Array.isArray(n) && n.length === 0 && Array.isArray(p) && p.length > 0) return p;
    return n;
  };
  return normalizeKanbanCardFull({
    ...next,
    labels: keepIfEmpty('labels'),
    assignees: keepIfEmpty('assignees'),
    checklists: keepIfEmpty('checklists'),
    comments: keepIfEmpty('comments'),
    attachments: keepIfEmpty('attachments'),
    watchers: keepIfEmpty('watchers'),
    links: keepIfEmpty('links'),
  });
}

async function fetchKanbanLinkPreview(
  linkType: KanbanCardLinkType,
  targetId: string,
): Promise<string | null> {
  try {
    switch (linkType) {
      case 'incident': {
        const inc = await incidentsApiService.getIncident(targetId);
        return inc.title || targetId;
      }
      case 'calendar_event': {
        const ev = await calendarApiService.getEventById(targetId);
        return ev.title || targetId;
      }
      case 'partnership': {
        const p = await partnershipsApiService.getPartnershipById(targetId);
        return p.summary?.slice(0, 140) || `Partnership ${targetId.slice(0, 8)}…`;
      }
      case 'partnership_creator': {
        const c = await partnershipsApiService.getCreatorById(targetId);
        return c.name;
      }
      case 'partnership_modpack': {
        const m = await partnershipsApiService.getModpackById(targetId);
        return m.name;
      }
      case 'build': {
        const b = await buildTeamApiService.getBuildItemById(targetId);
        return b.title || targetId;
      }
      case 'build_project': {
        const p = await buildTeamApiService.getProjectById(targetId);
        return p.title || targetId;
      }
      case 'modpack_note': {
        const n = await modpackNotesApiService.getNoteById(targetId);
        return (n.content || '').slice(0, 120) || targetId;
      }
      case 'kanban_card': {
        const c = await kanbanApiService.getCard(targetId);
        return c.title || targetId;
      }
      case 'appeal': {
        const detail = await appealsApiService.getAppealDetail(Number(targetId));
        return `#${detail.id} · ${detail.username}`;
      }
      case 'evidence': {
        const ev = await notesApiService.getEvidenceById(targetId);
        return ev.filename || targetId;
      }
      case 'build_team_member': {
        const m = await buildTeamApiService.getTeamMemberById(targetId);
        return m.display_name || targetId;
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

export const KanbanCardDetailPanel = ({
  cardId,
  boardId,
  boardData,
  onClose,
  onUpdated,
  onCardHydrated,
  canWrite,
  currentUserUuid,
  wsRefetchNonce = 0,
  cardIncidentsRefreshNonce = 0,
  cardEventsRefreshNonce = 0,
}: KanbanCardDetailPanelProps) => {
  const isMobile = useIsMobile();
  const { identity } = useAuth();

  // ── Core state ──────────────────────────────────────────────────────────
  const [card, setCard] = useState<KanbanCardFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<KanbanCardHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activity, setActivity] = useState<KanbanActivityLogEntry[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activeTab, setActiveTab] = useState<ActiveTab>('details');

  // ── Inline editing ──────────────────────────────────────────────────────
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, any>>({});

  // ── Busy / submitting states ────────────────────────────────────────────
  const [savingField, setSavingField] = useState<string | null>(null);
  const [checklistBusyId, setChecklistBusyId] = useState<string | null>(null);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [watchBusy, setWatchBusy] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [addAssigneeBusy, setAddAssigneeBusy] = useState(false);
  const [removeAssigneeBusy, setRemoveAssigneeBusy] = useState<string | null>(null);
  const [addLabelBusy, setAddLabelBusy] = useState(false);
  const [removeLabelBusy, setRemoveLabelBusy] = useState<string | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [newChecklistBusy, setNewChecklistBusy] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentContent, setEditCommentContent] = useState('');
  const [editCommentBusy, setEditCommentBusy] = useState(false);
  const [editChecklistTitleId, setEditChecklistTitleId] = useState<string | null>(null);
  const [editChecklistTitle, setEditChecklistTitle] = useState('');
  const [editChecklistTitleBusy, setEditChecklistTitleBusy] = useState(false);
  const [editChecklistItemId, setEditChecklistItemId] = useState<string | null>(null);
  const [editChecklistItemContent, setEditChecklistItemContent] = useState('');
  const [editChecklistItemBusy, setEditChecklistItemBusy] = useState(false);
  const [moveChecklistBusy, setMoveChecklistBusy] = useState<string | null>(null);
  const [moveChecklistItemBusy, setMoveChecklistItemBusy] = useState<string | null>(null);
  const [downloadingAttId, setDownloadingAttId] = useState<string | null>(null);

  // ── Confirmation modals ─────────────────────────────────────────────────
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showDeleteCommentId, setShowDeleteCommentId] = useState<string | null>(null);
  const [showDeleteAttachmentId, setShowDeleteAttachmentId] = useState<string | null>(null);
  const [showDeleteChecklistId, setShowDeleteChecklistId] = useState<string | null>(null);
  const [showDeleteChecklistItemId, setShowDeleteChecklistItemId] = useState<string | null>(null);
  const [showDeleteLinkId, setShowDeleteLinkId] = useState<string | null>(null);
  const [deleteItemBusy, setDeleteItemBusy] = useState(false);

  // ── Misc UI state ───────────────────────────────────────────────────────
  const [showAddLink, setShowAddLink] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [showAssigneePicker, setShowAssigneePicker] = useState(false);
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
  const debouncedAssigneeSearch = useDebounce(assigneeSearchQuery, 300);
  const [assigneeEsHits, setAssigneeEsHits] = useState<PlayerEsHit[]>([]);
  const [assigneeSearchLoading, setAssigneeSearchLoading] = useState(false);
  const [assigneeSearchError, setAssigneeSearchError] = useState<string | null>(null);
  const assigneeUsernameCacheVersion = useUsernameCacheVersion();
  const assigneeSearchRows = useMemo(
    () =>
      assigneeEsHits.map((h) => ({
        uuid: h._source.uuid,
        name:
          h._source.lastKnownName ||
          usernameCacheService.getName(h._source.uuid) ||
          h._source.uuid,
      })),
    [assigneeEsHits, assigneeUsernameCacheVersion],
  );

  useEffect(() => {
    if (!showAssigneePicker || !canWrite) {
      setAssigneeEsHits([]);
      setAssigneeSearchError(null);
      setAssigneeSearchLoading(false);
      return;
    }
    const q = debouncedAssigneeSearch.trim();
    if (q.length < 2) {
      setAssigneeEsHits([]);
      setAssigneeSearchError(null);
      setAssigneeSearchLoading(false);
      return;
    }
    let cancelled = false;
    setAssigneeSearchLoading(true);
    setAssigneeSearchError(null);
    void elasticsearchService
      .searchPlayers(q)
      .then((res) => {
        if (!cancelled) setAssigneeEsHits(res.hits?.hits ?? []);
      })
      .catch((e) => {
        if (!cancelled) {
          setAssigneeEsHits([]);
          setAssigneeSearchError(e instanceof Error ? e.message : 'Search failed');
        }
      })
      .finally(() => {
        if (!cancelled) setAssigneeSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showAssigneePicker, canWrite, debouncedAssigneeSearch]);

  useEffect(() => {
    if (assigneeEsHits.length === 0) return;
    void usernameCacheService.prefetchUsernames(assigneeEsHits.map((h) => h._source.uuid));
  }, [assigneeEsHits]);
  const [linkPreviewLabels, setLinkPreviewLabels] = useState<Record<string, string>>({});
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState<Record<string, string>>({});
  const [newChecklistTitle, setNewChecklistTitle] = useState('');
  const [newItemContent, setNewItemContent] = useState<Record<string, string>>({});
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    properties: true,
    description: true,
    assignees: true,
    labels: true,
    checklists: true,
    comments: true,
    attachments: true,
    links: true,
    watchers: true,
  });
  const [blockReasonInput, setBlockReasonInput] = useState('');
  const [showBlockInput, setShowBlockInput] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const labelDropdownRef = useRef<HTMLDivElement>(null);

  // ── Incident bridge state ───────────────────────────────────────────────
  /**
   * The list of incidents bridged to this card via the new Incident-Workspaces
   * integration (`GET /kanban/cards/{id}/incidents`). When non-empty, this card
   * has at least one bridge link and we route mutations through the
   * `*-with-sync` companions so per-link sync flags are honoured.
   *
   * The list is owned by `<CardIncidentsSection>` which calls `setLinkedIncidents`
   * via `onIncidentsChanged`. We keep a parallel copy here purely so the helpers
   * below can decide which endpoint to call without an extra fetch per mutation.
   *
   * NOTE: this is a *coarse proxy* for "any sync flag on" — the backend short-
   * circuits when no flag is actually set, so calling `*-with-sync` is harmless
   * even when no flag is on. The proxy avoids a per-link round trip.
   */
  const [linkedIncidents, setLinkedIncidents] = useState<Incident[]>([]);
  /**
   * Event Team bridge state — same pattern as incidents. The card-side
   * `*-with-sync` endpoints fan out to BOTH engines (incidents first, then
   * events), so either list being non-empty routes mutations through them.
   */
  const [linkedEvents, setLinkedEvents] = useState<EventItem[]>([]);
  const useIncidentSync = linkedIncidents.length > 0 || linkedEvents.length > 0;

  // ── Sync-routing helpers ────────────────────────────────────────────────
  const updateCardMaybeSync: typeof kanbanApiService.updateCard = useCallback(
    (id, body) =>
      useIncidentSync
        ? kanbanApiService.updateCardWithSync(id, body)
        : kanbanApiService.updateCard(id, body),
    [useIncidentSync],
  );

  const moveCardMaybeSync: typeof kanbanApiService.moveCard = useCallback(
    (id, body) =>
      useIncidentSync
        ? kanbanApiService.moveCardWithSync(id, body)
        : kanbanApiService.moveCard(id, body),
    [useIncidentSync],
  );

  const addAssigneeMaybeSync: typeof kanbanApiService.addAssignee = useCallback(
    (id, uuid) =>
      useIncidentSync
        ? kanbanApiService.addAssigneeWithSync(id, uuid)
        : kanbanApiService.addAssignee(id, uuid),
    [useIncidentSync],
  );

  const removeAssigneeMaybeSync: typeof kanbanApiService.removeAssignee = useCallback(
    (id, uuid) =>
      useIncidentSync
        ? kanbanApiService.removeAssigneeWithSync(id, uuid)
        : kanbanApiService.removeAssignee(id, uuid),
    [useIncidentSync],
  );

  const createCommentMaybeSync: typeof kanbanApiService.createComment = useCallback(
    (id, body) =>
      useIncidentSync
        ? kanbanApiService.addCommentWithSync(id, body)
        : kanbanApiService.createComment(id, body),
    [useIncidentSync],
  );

  // ── Derived state ───────────────────────────────────────────────────────
  const isWatching = useMemo(
    () => !!(card?.watchers && currentUserUuid && card.watchers.some(w => w.user_uuid === currentUserUuid)),
    [card?.watchers, currentUserUuid],
  );

  const listTitleById = useMemo(() => {
    const m: Record<string, string> = {};
    boardData?.lists.forEach(l => { m[l.id] = l.title; });
    return m;
  }, [boardData]);

  const swimlaneTitleById = useMemo(() => {
    const m: Record<string, string> = {};
    boardData?.swimlanes.forEach(s => { m[s.id] = s.title; });
    return m;
  }, [boardData]);

  const availableLabels = useMemo(() => {
    if (!boardData || !card) return [];
    const appliedIds = new Set((card.labels ?? []).map((l) => l.id));
    return boardData.labels.filter((l) => !appliedIds.has(l.id));
  }, [boardData, card]);

  const assigneeUuidSet = useMemo(
    () => new Set((card?.assignees ?? []).map((a) => a.user_uuid)),
    [card?.assignees],
  );

  const boardCardRow = useMemo(
    () => boardData?.cards?.find((c) => c.id === cardId),
    [boardData, cardId],
  );

  /** Primitive only — board WS updates replace `boardCardRow` by reference every tick; do not use the object in effect deps. */
  const boardCommentCount = boardCardRow?.comment_count;

  const sortedLists = useMemo(
    () => [...(boardData?.lists || [])].sort((a, b) => a.position - b.position),
    [boardData],
  );

  const sortedSwimlanes = useMemo(
    () => [...(boardData?.swimlanes || [])].sort((a, b) => a.position - b.position),
    [boardData],
  );

  const linkPreviewFetchTypes: KanbanCardLinkType[] = useMemo(
    () => [
      'incident',
      'calendar_event',
      'partnership',
      'partnership_creator',
      'partnership_modpack',
      'build',
      'build_project',
      'build_team_member',
      'modpack_note',
      'kanban_card',
      'appeal',
      'evidence',
    ],
    [],
  );

  useEffect(() => {
    if (!card?.links?.length) {
      setLinkPreviewLabels({});
      return;
    }
    const toFetch = card.links.filter((l) => linkPreviewFetchTypes.includes(l.link_type));
    if (toFetch.length === 0) {
      setLinkPreviewLabels({});
      return;
    }
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        toFetch.map(async (l) => {
          const label = await fetchKanbanLinkPreview(l.link_type, l.link_target_id);
          return [l.id, label] as const;
        }),
      );
      if (cancelled) return;
      const next: Record<string, string> = {};
      for (const [id, label] of entries) {
        if (label) next[id] = label;
      }
      setLinkPreviewLabels(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [card?.links, linkPreviewFetchTypes]);

  // ── Data loading ────────────────────────────────────────────────────────
  const reloadCard = useCallback(async () => {
    const c = await kanbanApiService.getCard(cardId);
    setCard((prev) => mergeKanbanCardFull(prev, c));
  }, [cardId]);

  // Section-specific refresh helpers (use individual list endpoints for efficiency)
  const refreshComments = useCallback(async () => {
    const comments = await kanbanApiService.listComments(cardId);
    setCard((prev) =>
      prev ? normalizeKanbanCardFull({ ...prev, comments: Array.isArray(comments) ? comments : [] }) : prev,
    );
  }, [cardId]);

  const refreshChecklists = useCallback(async () => {
    const checklists = await kanbanApiService.listChecklists(cardId);
    setCard((prev) =>
      prev ? normalizeKanbanCardFull({ ...prev, checklists: Array.isArray(checklists) ? checklists : [] }) : prev,
    );
  }, [cardId]);

  const refreshAttachments = useCallback(async () => {
    const attachments = await kanbanApiService.listAttachments(cardId);
    setCard((prev) =>
      prev ? normalizeKanbanCardFull({ ...prev, attachments: Array.isArray(attachments) ? attachments : [] }) : prev,
    );
  }, [cardId]);

  const refreshLabels = useCallback(async () => {
    const labels = await kanbanApiService.listCardLabels(cardId);
    setCard((prev) =>
      prev ? normalizeKanbanCardFull({ ...prev, labels: Array.isArray(labels) ? labels : [] }) : prev,
    );
  }, [cardId]);

  const refreshAssignees = useCallback(async () => {
    const assignees = await kanbanApiService.listAssignees(cardId);
    setCard((prev) =>
      prev ? normalizeKanbanCardFull({ ...prev, assignees: Array.isArray(assignees) ? assignees : [] }) : prev,
    );
  }, [cardId]);

  const refreshWatchers = useCallback(async () => {
    const watchers = await kanbanApiService.listWatchers(cardId);
    setCard((prev) =>
      prev ? normalizeKanbanCardFull({ ...prev, watchers: Array.isArray(watchers) ? watchers : [] }) : prev,
    );
  }, [cardId]);

  const refreshLinks = useCallback(async () => {
    const links = await kanbanApiService.listCardLinks(cardId);
    setCard((prev) =>
      prev ? normalizeKanbanCardFull({ ...prev, links: Array.isArray(links) ? links : [] }) : prev,
    );
  }, [cardId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    kanbanApiService
      .getCard(cardId)
      .then((c) => {
        if (!cancelled) setCard((prev) => mergeKanbanCardFull(prev, c));
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cardId, wsRefetchNonce]);

  useEffect(() => {
    setActiveTab('details');
    setHistory([]);
    setActivity([]);
    setActivityPage(1);
    setActivityTotal(0);
    setHistoryLoading(false);
    setActivityLoading(false);
  }, [cardId]);

  /** History + activity: dedicated GETs when the card panel loads (not only when tabs open). */
  useEffect(() => {
    if (loading) return;
    const id = cardId;
    let cancelled = false;
    void (async () => {
      try {
        const [h, act] = await Promise.all([
          kanbanApiService.getCardHistory(id, { limit: 50 }),
          kanbanApiService.getCardActivity(id, { page: 1, limit: 25 }),
        ]);
        if (cancelled || id !== cardId) return;
        setHistory(Array.isArray(h) ? h : []);
        setActivity(act.results);
        setActivityTotal(act.total);
        setActivityPage(1);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, loading, wsRefetchNonce]);

  const attachmentListKey = useMemo(
    () => (card?.attachments ?? []).map((a) => `${a.id}:${a.content_type}:${a.size}`).join('|'),
    [card?.attachments],
  );

  useEffect(() => {
    if (card) onCardHydrated?.(card);
  }, [card, onCardHydrated]);

  const attachmentBlobUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (!card?.attachments?.length) {
      attachmentBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      attachmentBlobUrlsRef.current = [];
      setAttachmentPreviewUrls({});
      return;
    }
    const imageAttachments = card.attachments.filter((a) => isImageContentType(a.content_type));
    if (imageAttachments.length === 0) {
      attachmentBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      attachmentBlobUrlsRef.current = [];
      setAttachmentPreviewUrls({});
      return;
    }
    let cancelled = false;
    attachmentBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    attachmentBlobUrlsRef.current = [];
    void (async () => {
      const next: Record<string, string> = {};
      for (const att of imageAttachments) {
        if (cancelled) break;
        try {
          const res = await kanbanApiService.downloadAttachment(att.id);
          const blob = await res.blob();
          if (cancelled) break;
          const url = URL.createObjectURL(blob);
          attachmentBlobUrlsRef.current.push(url);
          next[att.id] = url;
        } catch {
          /* preview optional */
        }
      }
      if (!cancelled) setAttachmentPreviewUrls(next);
    })();
    return () => {
      cancelled = true;
      attachmentBlobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      attachmentBlobUrlsRef.current = [];
    };
  }, [attachmentListKey, cardId]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const h = await kanbanApiService.getCardHistory(cardId, { limit: 50 });
      setHistory(Array.isArray(h) ? h : []);
    } catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  }, [cardId]);

  const loadActivity = useCallback(async (page = 1) => {
    setActivityLoading(true);
    try {
      const resp = await kanbanApiService.getCardActivity(cardId, { page, limit: 25 });
      if (page === 1) {
        setActivity(resp.results);
      } else {
        setActivity(prev => [...prev, ...resp.results]);
      }
      setActivityTotal(resp.total);
      setActivityPage(page);
    } catch (e) { console.error(e); }
    finally { setActivityLoading(false); }
  }, [cardId]);

  useEffect(() => {
    if (activeTab === 'history' && history.length === 0 && !historyLoading) void loadHistory();
    if (activeTab === 'activity' && activity.length === 0 && !activityLoading) void loadActivity(1);
  }, [activeTab, history.length, activity.length, loadHistory, loadActivity, historyLoading, activityLoading]);

  // ── Close on Escape ─────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // ── Close label dropdown on outside click ───────────────────────────────
  useEffect(() => {
    if (!showLabelDropdown) return;
    const handler = (e: MouseEvent) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(e.target as Node)) {
        setShowLabelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showLabelDropdown]);

  useEffect(() => {
    if (!expandedSections.assignees) {
      setShowAssigneePicker(false);
      setAssigneeSearchQuery('');
    }
  }, [expandedSections.assignees]);

  useEffect(() => {
    if (!expandedSections.comments || !cardId) return;
    if (boardCommentCount === 0) {
      setCard((prev) =>
        prev && prev.id === cardId ? normalizeKanbanCardFull({ ...prev, comments: [] }) : prev,
      );
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const comments = await kanbanApiService.listComments(cardId);
        if (cancelled) return;
        setCard((prev) =>
          prev && prev.id === cardId
            ? normalizeKanbanCardFull({ ...prev, comments: Array.isArray(comments) ? comments : [] })
            : prev,
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, expandedSections.comments, boardCommentCount, wsRefetchNonce]);

  useEffect(() => {
    if (!expandedSections.checklists || !cardId) return;
    let cancelled = false;
    void (async () => {
      try {
        const checklists = await kanbanApiService.listChecklists(cardId);
        if (cancelled) return;
        setCard((prev) =>
          prev && prev.id === cardId
            ? normalizeKanbanCardFull({ ...prev, checklists: Array.isArray(checklists) ? checklists : [] })
            : prev,
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, expandedSections.checklists, wsRefetchNonce]);

  useEffect(() => {
    if (!expandedSections.assignees || !cardId) return;
    let cancelled = false;
    void (async () => {
      try {
        const assignees = await kanbanApiService.listAssignees(cardId);
        if (cancelled) return;
        setCard((prev) =>
          prev && prev.id === cardId
            ? normalizeKanbanCardFull({ ...prev, assignees: Array.isArray(assignees) ? assignees : [] })
            : prev,
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, expandedSections.assignees, wsRefetchNonce]);

  useEffect(() => {
    if (!expandedSections.labels || !cardId) return;
    let cancelled = false;
    void (async () => {
      try {
        const labels = await kanbanApiService.listCardLabels(cardId);
        if (cancelled) return;
        setCard((prev) =>
          prev && prev.id === cardId
            ? normalizeKanbanCardFull({ ...prev, labels: Array.isArray(labels) ? labels : [] })
            : prev,
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, expandedSections.labels, wsRefetchNonce]);

  useEffect(() => {
    if (!expandedSections.attachments || !cardId) return;
    let cancelled = false;
    void (async () => {
      try {
        const attachments = await kanbanApiService.listAttachments(cardId);
        if (cancelled) return;
        setCard((prev) =>
          prev && prev.id === cardId
            ? normalizeKanbanCardFull({ ...prev, attachments: Array.isArray(attachments) ? attachments : [] })
            : prev,
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, expandedSections.attachments, wsRefetchNonce]);

  useEffect(() => {
    if (!expandedSections.links || !cardId) return;
    let cancelled = false;
    void (async () => {
      try {
        const links = await kanbanApiService.listCardLinks(cardId);
        if (cancelled) return;
        setCard((prev) =>
          prev && prev.id === cardId
            ? normalizeKanbanCardFull({ ...prev, links: Array.isArray(links) ? links : [] })
            : prev,
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, expandedSections.links, wsRefetchNonce]);

  useEffect(() => {
    if (!expandedSections.watchers || !cardId) return;
    let cancelled = false;
    void (async () => {
      try {
        const watchers = await kanbanApiService.listWatchers(cardId);
        if (cancelled) return;
        setCard((prev) =>
          prev && prev.id === cardId
            ? normalizeKanbanCardFull({ ...prev, watchers: Array.isArray(watchers) ? watchers : [] })
            : prev,
        );
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cardId, expandedSections.watchers, wsRefetchNonce]);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const afterMutation = useCallback(async () => {
    await reloadCard();
    onUpdated();
  }, [reloadCard, onUpdated]);

  // Section-specific mutation helpers: faster refresh for individual sections
  const afterCommentMutation = useCallback(async () => { await refreshComments(); onUpdated(); }, [refreshComments, onUpdated]);
  const afterChecklistMutation = useCallback(async () => { await refreshChecklists(); onUpdated(); }, [refreshChecklists, onUpdated]);
  const afterAttachmentMutation = useCallback(async () => { await refreshAttachments(); onUpdated(); }, [refreshAttachments, onUpdated]);
  const afterLabelMutation = useCallback(async () => { await refreshLabels(); onUpdated(); }, [refreshLabels, onUpdated]);
  const afterAssigneeMutation = useCallback(async () => { await refreshAssignees(); onUpdated(); }, [refreshAssignees, onUpdated]);
  const afterWatcherMutation = useCallback(async () => { await refreshWatchers(); onUpdated(); }, [refreshWatchers, onUpdated]);
  const afterLinkMutation = useCallback(async () => { await refreshLinks(); onUpdated(); }, [refreshLinks, onUpdated]);

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const startEditing = (field: string, currentValue: string | number | null) => {
    setEditingField(field);
    setEditValues(prev => ({ ...prev, [field]: currentValue }));
  };

  const cancelEditing = () => {
    setEditingField(null);
  };

  // ── Card CRUD ───────────────────────────────────────────────────────────
  const handleSaveField = async (field: string) => {
    if (!card || !canWrite) return;
    setSavingField(field);
    try {
      const value = editValues[field];
      if (field === 'title') {
        await updateCardMaybeSync(card.id, { title: value });
      } else if (field === 'description') {
        await updateCardMaybeSync(card.id, { description: value || null });
      } else if (field === 'card_type') {
        await updateCardMaybeSync(card.id, { card_type: value });
      } else if (field === 'priority') {
        await updateCardMaybeSync(card.id, { priority: Number(value) });
      } else if (field === 'class_of_service') {
        await updateCardMaybeSync(card.id, { class_of_service: value });
      } else if (field === 'due_date') {
        const ts = value ? new Date(value).getTime() : null;
        await updateCardMaybeSync(card.id, { due_date: ts });
      } else if (field === 'start_date') {
        const ts = value ? new Date(value).getTime() : null;
        await updateCardMaybeSync(card.id, { start_date: ts });
      } else if (field === 'cover_color') {
        await updateCardMaybeSync(card.id, { cover_color: value || null });
      }
      await afterMutation();
      setEditingField(null);
    } catch (e) { console.error(e); }
    finally { setSavingField(null); }
  };

  const handleMoveCard = async (listId?: string, swimlaneId?: string | null) => {
    if (!card || !canWrite) return;
    setMoveBusy(true);
    try {
      const req: Record<string, unknown> = {};
      if (listId !== undefined) req.list_id = listId;
      else if (swimlaneId !== undefined) req.list_id = card.list_id;
      if (swimlaneId !== undefined) req.swimlane_id = swimlaneId;
      await moveCardMaybeSync(card.id, req);
      const fresh = await kanbanApiService.getCard(card.id);
      setCard((prev) => mergeKanbanCardFull(prev, fresh));
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setMoveBusy(false); }
  };

  const handleArchiveCard = async () => {
    if (!card || !canWrite) return;
    setArchiveBusy(true);
    try {
      await kanbanApiService.archiveCard(card.id, card.is_archived === 0);
      await afterMutation();
      setShowArchiveConfirm(false);
      if (card.is_archived === 0) onClose();
    } catch (e) { console.error(e); }
    finally { setArchiveBusy(false); }
  };

  const handleDeleteCard = async () => {
    if (!card || !canWrite) return;
    setDeleteBusy(true);
    try {
      await kanbanApiService.deleteCard(card.id);
      onUpdated();
      onClose();
    } catch (e) { console.error(e); }
    finally { setDeleteBusy(false); }
  };

  const handleBlockCard = async (block: boolean, reason?: string) => {
    if (!card || !canWrite) return;
    setBlockBusy(true);
    try {
      await kanbanApiService.blockCard(card.id, {
        is_blocked: block,
        blocked_reason: reason || null,
      });
      await afterMutation();
      setShowBlockInput(false);
      setBlockReasonInput('');
    } catch (e) { console.error(e); }
    finally { setBlockBusy(false); }
  };

  // ── Watch ───────────────────────────────────────────────────────────────
  const handleToggleWatch = async () => {
    if (!card) return;
    setWatchBusy(true);
    try {
      if (isWatching) {
        await kanbanApiService.unwatchCard(card.id);
      } else {
        await kanbanApiService.watchCard(card.id);
      }
      await afterWatcherMutation();
    } catch (e) { console.error(e); }
    finally { setWatchBusy(false); }
  };

  // ── Assignees ───────────────────────────────────────────────────────────
  const handleAddAssigneeByUuid = async (uuid: string) => {
    if (!card || !canWrite) return;
    const trimmed = uuid.trim();
    if (!trimmed || assigneeUuidSet.has(trimmed)) return;
    setAddAssigneeBusy(true);
    try {
      await addAssigneeMaybeSync(card.id, trimmed);
      setAssigneeSearchQuery('');
      setShowAssigneePicker(false);
      await afterAssigneeMutation();
    } catch (e) { console.error(e); }
    finally { setAddAssigneeBusy(false); }
  };

  const handleRemoveAssignee = async (uuid: string) => {
    if (!card || !canWrite) return;
    setRemoveAssigneeBusy(uuid);
    try {
      await removeAssigneeMaybeSync(card.id, uuid);
      await afterAssigneeMutation();
    } catch (e) { console.error(e); }
    finally { setRemoveAssigneeBusy(null); }
  };

  // ── Labels ──────────────────────────────────────────────────────────────
  const handleAddLabel = async (labelId: string) => {
    if (!card || !canWrite) return;
    setAddLabelBusy(true);
    try {
      await kanbanApiService.addLabelToCard(card.id, labelId);
      setShowLabelDropdown(false);
      await afterLabelMutation();
    } catch (e) { console.error(e); }
    finally { setAddLabelBusy(false); }
  };

  const handleRemoveLabel = async (labelId: string) => {
    if (!card || !canWrite) return;
    setRemoveLabelBusy(labelId);
    try {
      await kanbanApiService.removeLabelFromCard(card.id, labelId);
      await afterLabelMutation();
    } catch (e) { console.error(e); }
    finally { setRemoveLabelBusy(null); }
  };

  // ── Checklists ──────────────────────────────────────────────────────────
  const handleCreateChecklist = async () => {
    if (!card || !canWrite) return;
    setNewChecklistBusy(true);
    try {
      await kanbanApiService.createChecklist(card.id, { title: newChecklistTitle.trim() || 'Checklist' });
      setNewChecklistTitle('');
      await afterChecklistMutation();
    } catch (e) { console.error(e); }
    finally { setNewChecklistBusy(false); }
  };

  const handleUpdateChecklistTitle = async (checklistId: string) => {
    if (!canWrite || !editChecklistTitle.trim()) return;
    setEditChecklistTitleBusy(true);
    try {
      // Fetch fresh checklist to verify it still exists
      await kanbanApiService.getChecklist(checklistId);
      await kanbanApiService.updateChecklist(checklistId, { title: editChecklistTitle.trim() });
      setEditChecklistTitleId(null);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setEditChecklistTitleBusy(false); }
  };

  const handleDeleteChecklist = async (checklistId: string) => {
    if (!canWrite) return;
    setDeleteItemBusy(true);
    try {
      await kanbanApiService.deleteChecklist(checklistId);
      setShowDeleteChecklistId(null);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setDeleteItemBusy(false); }
  };

  const handleMoveChecklist = async (checklistId: string, position: number) => {
    if (!canWrite) return;
    setMoveChecklistBusy(checklistId);
    try {
      await kanbanApiService.moveChecklist(checklistId, position);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setMoveChecklistBusy(null); }
  };

  const handleCreateChecklistItem = async (checklistId: string) => {
    const content = newItemContent[checklistId]?.trim();
    if (!card || !canWrite || !content) return;
    setChecklistBusyId(`new-${checklistId}`);
    try {
      await kanbanApiService.createChecklistItem(checklistId, { content });
      setNewItemContent(prev => ({ ...prev, [checklistId]: '' }));
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setChecklistBusyId(null); }
  };

  const handleToggleChecklistItem = async (itemId: string, nextChecked: boolean) => {
    if (!canWrite) return;
    setChecklistBusyId(itemId);
    try {
      await kanbanApiService.toggleChecklistItem(itemId, nextChecked);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setChecklistBusyId(null); }
  };

  const handleUpdateChecklistItem = async (itemId: string) => {
    if (!canWrite || !editChecklistItemContent.trim()) return;
    setEditChecklistItemBusy(true);
    try {
      await kanbanApiService.updateChecklistItem(itemId, { content: editChecklistItemContent.trim() });
      setEditChecklistItemId(null);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setEditChecklistItemBusy(false); }
  };

  const handleMoveChecklistItem = async (itemId: string, position: number) => {
    if (!canWrite) return;
    setMoveChecklistItemBusy(itemId);
    try {
      await kanbanApiService.moveChecklistItem(itemId, position);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setMoveChecklistItemBusy(null); }
  };

  const handleDeleteChecklistItem = async (itemId: string) => {
    if (!canWrite) return;
    setDeleteItemBusy(true);
    try {
      await kanbanApiService.deleteChecklistItem(itemId);
      setShowDeleteChecklistItemId(null);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setDeleteItemBusy(false); }
  };

  // ── Comments ────────────────────────────────────────────────────────────
  const handleCreateComment = async () => {
    if (!card || !canWrite || !newComment.trim()) return;
    setCommentSubmitting(true);
    try {
      await createCommentMaybeSync(card.id, { content: newComment.trim() });
      setNewComment('');
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setCommentSubmitting(false); }
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!canWrite || !editCommentContent.trim()) return;
    setEditCommentBusy(true);
    try {
      // Fetch fresh comment data to verify it still exists before updating
      await kanbanApiService.getComment(commentId);
      await kanbanApiService.updateComment(commentId, { content: editCommentContent.trim() });
      setEditingCommentId(null);
      await afterCommentMutation();
    } catch (e) { console.error(e); }
    finally { setEditCommentBusy(false); }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!canWrite) return;
    setDeleteItemBusy(true);
    try {
      await kanbanApiService.deleteComment(commentId);
      setShowDeleteCommentId(null);
      await afterCommentMutation();
    } catch (e) { console.error(e); }
    finally { setDeleteItemBusy(false); }
  };

  // ── Attachments ─────────────────────────────────────────────────────────
  const handleUploadAttachments = async (files: FileList | null) => {
    if (!card || !canWrite || !files || files.length === 0) return;
    setUploadingFiles(true);
    try {
      await kanbanApiService.uploadAttachments(card.id, Array.from(files));
      await afterAttachmentMutation();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (e) { console.error(e); }
    finally { setUploadingFiles(false); }
  };

  const handleDownloadAttachment = async (attachmentId: string, filename: string) => {
    setDownloadingAttId(attachmentId);
    try {
      const response = await kanbanApiService.downloadAttachment(attachmentId);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloadingAttId(null); }
  };

  const handleOpenAttachmentInNewTab = async (attachmentId: string) => {
    try {
      const response = await kanbanApiService.downloadAttachment(attachmentId);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 120_000);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!canWrite) return;
    setDeleteItemBusy(true);
    try {
      await kanbanApiService.deleteAttachment(attachmentId);
      setShowDeleteAttachmentId(null);
      await afterMutation();
    } catch (e) { console.error(e); }
    finally { setDeleteItemBusy(false); }
  };

  // ── Links ───────────────────────────────────────────────────────────────
  const handleDeleteLink = async (linkId: string) => {
    if (!canWrite) return;
    setDeleteItemBusy(true);
    try {
      await kanbanApiService.deleteCardLink(linkId);
      setShowDeleteLinkId(null);
      await afterLinkMutation();
    } catch (e) { console.error(e); }
    finally { setDeleteItemBusy(false); }
  };

  // ── Render helpers ──────────────────────────────────────────────────────
  const renderSectionHeader = (key: string, icon: React.ReactNode, label: string, count?: number) => (
    <button
      type="button"
      onClick={() => toggleSection(key)}
      className="flex items-center gap-2 w-full text-left group min-w-0"
      aria-expanded={!!expandedSections[key]}
    >
      <span className="text-accent-300 w-4 h-4 flex items-center justify-center shrink-0">{icon}</span>
      <span className="text-sm font-semibold text-white truncate">
        {label}
      </span>
      {count !== undefined && count > 0 && (
        <span className="badge badge-neutral">{count}</span>
      )}
      {expandedSections[key] ? (
        <ChevronDown className="w-4 h-4 text-glass-500 ml-auto shrink-0 group-hover:text-glass-300 transition-colors" />
      ) : (
        <ChevronRight className="w-4 h-4 text-glass-500 ml-auto shrink-0 group-hover:text-glass-300 transition-colors" />
      )}
    </button>
  );

  const renderPropertyRow = (label: string, content: React.ReactNode) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-glass-200/10 last:border-0">
      <span className="text-xs font-medium text-glass-400 flex-shrink-0">{label}</span>
      <div className="text-right min-w-0">{content}</div>
    </div>
  );

  const tsToDateInputValue = (ts: number | null) => {
    if (!ts) return '';
    const d = parseDate(ts);
    return d ? format(d, 'yyyy-MM-dd') : '';
  };

  // ── Tab: Details ────────────────────────────────────────────────────────
  const renderDetails = () => {
    if (!card) return null;

    return (
      <div className="space-y-4 animate-in slide-in-from-right-4 duration-200">
        {/* Linked incidents (Incident-Workspaces bridge — distinct from the legacy "Links" section). */}
        <CardIncidentsSection
          cardId={card.id}
          cardTitle={card.title}
          cardDescription={card.description}
          cardPriority={card.priority}
          canWrite={canWrite}
          refreshNonce={cardIncidentsRefreshNonce}
          onIncidentsChanged={setLinkedIncidents}
        />

        {/* Linked events (Event-Workspaces bridge — mirrors the incidents integration). */}
        <CardEventsSection
          cardId={card.id}
          cardTitle={card.title}
          cardDescription={card.description}
          cardPriority={card.priority}
          canWrite={canWrite}
          refreshNonce={cardEventsRefreshNonce}
          onEventsChanged={setLinkedEvents}
        />

        {/* Properties */}
        <div className="glass-card p-4 space-y-1">
          {renderSectionHeader('properties', <Settings className="w-3.5 h-3.5" />, 'Properties')}
          {expandedSections.properties && (
            <div className="pt-2">
              {/* Status / List */}
              {renderPropertyRow('List', canWrite ? (
                <select
                  className="glass-select text-xs !min-h-0 py-1 px-2 min-w-[120px]"
                  value={card.list_id}
                  onChange={e => void handleMoveCard(e.target.value)}
                  disabled={moveBusy}
                >
                  {sortedLists.map(l => (
                    <option key={l.id} value={l.id}>{l.title}</option>
                  ))}
                </select>
              ) : (
                <span className="text-sm text-glass-200">{listTitleById[card.list_id] ?? card.list_id}</span>
              ))}

              {/* Swimlane */}
              {sortedSwimlanes.length > 0 && renderPropertyRow('Swimlane', canWrite ? (
                <select
                  className="glass-select text-xs !min-h-0 py-1 px-2 min-w-[120px]"
                  value={card.swimlane_id ?? ''}
                  onChange={e => void handleMoveCard(card.list_id, e.target.value || null)}
                  disabled={moveBusy}
                >
                  <option value="">None</option>
                  {sortedSwimlanes.map(s => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
              ) : (
                <span className="text-sm text-glass-200">
                  {card.swimlane_id ? (swimlaneTitleById[card.swimlane_id] ?? card.swimlane_id) : 'None'}
                </span>
              ))}

              {/* Card Type */}
              {renderPropertyRow('Card Type', canWrite ? (
                <select
                  className="glass-select text-xs !min-h-0 py-1 px-2 min-w-[120px]"
                  value={editingField === 'card_type' ? editValues.card_type : card.card_type}
                  onChange={e => {
                    setEditValues(prev => ({ ...prev, card_type: e.target.value }));
                    setEditingField('card_type');
                    void (async () => {
                      setSavingField('card_type');
                      try {
                        await updateCardMaybeSync(card.id, { card_type: e.target.value as KanbanCardType });
                        await afterMutation();
                      } catch (err) { console.error(err); }
                      finally { setSavingField(null); setEditingField(null); }
                    })();
                  }}
                  disabled={savingField === 'card_type'}
                >
                  {KANBAN_CARD_TYPES.map(t => (
                    <option key={t} value={t}>{t.replace('_', ' ')}</option>
                  ))}
                </select>
              ) : (
                <KanbanCardTypeBadge type={card.card_type} />
              ))}

              {/* Priority */}
              {renderPropertyRow('Priority', canWrite ? (
                editingField === 'priority' ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min={1}
                      max={10}
                      className="glass-input text-xs !min-h-0 py-1 px-2 w-16"
                      value={editValues.priority ?? card.priority}
                      onChange={e => setEditValues(prev => ({ ...prev, priority: e.target.value }))}
                      onKeyDown={e => {
                        if (e.key === 'Enter') void handleSaveField('priority');
                        if (e.key === 'Escape') cancelEditing();
                      }}
                      autoFocus
                    />
                    <button type="button" onClick={() => void handleSaveField('priority')} className="btn-icon btn-icon-sm text-green-400 hover:text-green-300" disabled={savingField === 'priority'} aria-label="Save priority" title="Save">
                      <Save className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={cancelEditing} className="btn-icon btn-icon-sm" aria-label="Cancel" title="Cancel">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <button type="button" onClick={() => startEditing('priority', card.priority)} className="hover:bg-glass-100/10 rounded px-1 transition-colors" title="Edit priority" aria-label="Edit priority">
                    <KanbanPriorityBadge priority={card.priority} />
                  </button>
                )
              ) : (
                <KanbanPriorityBadge priority={card.priority} />
              ))}

              {/* Class of Service */}
              {renderPropertyRow('Class of Service', canWrite ? (
                <select
                  className="glass-select text-xs !min-h-0 py-1 px-2 min-w-[120px]"
                  value={card.class_of_service}
                  onChange={e => {
                    void (async () => {
                      setSavingField('class_of_service');
                      try {
                        await updateCardMaybeSync(card.id, { class_of_service: e.target.value as KanbanClassOfService });
                        await afterMutation();
                      } catch (err) { console.error(err); }
                      finally { setSavingField(null); }
                    })();
                  }}
                  disabled={savingField === 'class_of_service'}
                >
                  {KANBAN_CLASSES_OF_SERVICE.map(c => (
                    <option key={c} value={c}>{c.replace('_', ' ')}</option>
                  ))}
                </select>
              ) : (
                <KanbanClassOfServiceIndicator classOfService={card.class_of_service} showLabel />
              ))}

              {/* Due Date */}
              {renderPropertyRow('Due Date', canWrite ? (
                <input
                  type="date"
                  className="glass-input text-xs !min-h-0 py-1 px-2"
                  value={tsToDateInputValue(card.due_date)}
                  onChange={e => {
                    const ts = e.target.value ? new Date(e.target.value).getTime() : null;
                    void (async () => {
                      setSavingField('due_date');
                      try {
                        await updateCardMaybeSync(card.id, { due_date: ts });
                        await afterMutation();
                      } catch (err) { console.error(err); }
                      finally { setSavingField(null); }
                    })();
                  }}
                  disabled={savingField === 'due_date'}
                />
              ) : (
                <span className="text-sm text-glass-200">
                  {card.due_date ? safeFormat(card.due_date, 'PPP') : '—'}
                </span>
              ))}

              {/* Start Date */}
              {renderPropertyRow('Start Date', canWrite ? (
                <input
                  type="date"
                  className="glass-input text-xs !min-h-0 py-1 px-2"
                  value={tsToDateInputValue(card.start_date)}
                  onChange={e => {
                    const ts = e.target.value ? new Date(e.target.value).getTime() : null;
                    void (async () => {
                      setSavingField('start_date');
                      try {
                        await updateCardMaybeSync(card.id, { start_date: ts });
                        await afterMutation();
                      } catch (err) { console.error(err); }
                      finally { setSavingField(null); }
                    })();
                  }}
                  disabled={savingField === 'start_date'}
                />
              ) : (
                <span className="text-sm text-glass-200">
                  {card.start_date ? safeFormat(card.start_date, 'PPP') : '—'}
                </span>
              ))}

              {/* Blocked status */}
              {renderPropertyRow('Blocked', (
                <div className="flex items-center gap-2">
                  {card.is_blocked === 1 ? (
                    <span className="badge badge-danger badge-dot max-w-full">
                      <span className="truncate">Blocked{card.blocked_reason ? `: ${card.blocked_reason}` : ''}</span>
                    </span>
                  ) : (
                    <span className="text-xs text-glass-500">Not blocked</span>
                  )}
                </div>
              ))}

              {/* Cover Color */}
              {renderPropertyRow('Cover Color', canWrite ? (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    className="w-6 h-6 rounded border border-glass-200/20 cursor-pointer bg-transparent"
                    value={card.cover_color || '#1a1a2e'}
                    onChange={e => {
                      void (async () => {
                        setSavingField('cover_color');
                        try {
                          await updateCardMaybeSync(card.id, { cover_color: e.target.value });
                          await afterMutation();
                        } catch (err) { console.error(err); }
                        finally { setSavingField(null); }
                      })();
                    }}
                    disabled={savingField === 'cover_color'}
                  />
                  {card.cover_color && (
                    <button
                      type="button"
                      className="btn-ghost btn-xs"
                      onClick={() => {
                        void (async () => {
                          setSavingField('cover_color');
                          try {
                            await updateCardMaybeSync(card.id, { cover_color: null });
                            await afterMutation();
                          } catch (err) { console.error(err); }
                          finally { setSavingField(null); }
                        })();
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              ) : (
                <span className="text-sm text-glass-200">
                  {card.cover_color ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="w-4 h-4 rounded border border-glass-200/20" style={{ backgroundColor: card.cover_color }} />
                      {card.cover_color}
                    </span>
                  ) : '—'}
                </span>
              ))}

              {/* Timestamps */}
              {renderPropertyRow('Created', (
                <span className="text-xs text-glass-400">{safeFormat(card.created_at, 'PPp')}</span>
              ))}
              {card.updated_at && renderPropertyRow('Updated', (
                <span className="text-xs text-glass-400">{safeFormat(card.updated_at, 'PPp')}</span>
              ))}
            </div>
          )}
        </div>

        {/* Description */}
        <div className="glass-card p-4 space-y-2">
          {renderSectionHeader('description', <Edit3 className="w-3.5 h-3.5" />, 'Description')}
          {expandedSections.description && (
            <div className="pt-2">
              {editingField === 'description' ? (
                <div className="space-y-2">
                  <textarea
                    className="glass-input w-full text-sm min-h-[120px] resize-y"
                    value={editValues.description ?? ''}
                    onChange={e => setEditValues(prev => ({ ...prev, description: e.target.value }))}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => void handleSaveField('description')}
                      disabled={savingField === 'description'}
                      className="glass-button-sm"
                    >
                      {savingField === 'description' ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Save
                    </button>
                    <button type="button" onClick={cancelEditing} className="glass-button-secondary btn-xs">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="group relative">
                  {card.description ? (
                    <MarkdownRenderer content={card.description} className="text-sm" />
                  ) : (
                    <p className="text-sm text-glass-500 italic">No description</p>
                  )}
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => startEditing('description', card.description ?? '')}
                      className="btn-icon btn-icon-sm absolute top-0 right-0 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity"
                      aria-label="Edit description"
                      title="Edit description"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Assignees */}
        <div className="glass-card p-4 space-y-2">
          {renderSectionHeader('assignees', <Users className="w-3.5 h-3.5" />, 'Assignees', card.assignees.length)}
          {expandedSections.assignees && (
            <div className="pt-2 space-y-2">
              {card.assignees.length === 0 ? (
                <p className="text-sm text-glass-500">No assignees</p>
              ) : (
                <ul className="space-y-2">
                  {card.assignees.map(a => (
                    <li key={a.user_uuid} className="flex items-center justify-between gap-2">
                      <PlayerProfile uuid={a.user_uuid} inline showUuid={false} />
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => void handleRemoveAssignee(a.user_uuid)}
                          disabled={removeAssigneeBusy === a.user_uuid}
                          className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                          aria-label="Remove assignee"
                          title="Remove assignee"
                        >
                          {removeAssigneeBusy === a.user_uuid ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {canWrite && (
                <div className="space-y-2 pt-1">
                  {identity?.raw?.custom?.minecraft_uuid &&
                    !assigneeUuidSet.has(identity.raw.custom.minecraft_uuid) && (
                      <button
                        type="button"
                        onClick={() => {
                          const u = identity?.raw?.custom?.minecraft_uuid;
                          if (u) void handleAddAssigneeByUuid(u);
                        }}
                        disabled={addAssigneeBusy}
                        className="glass-button-sm w-full"
                      >
                        {addAssigneeBusy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                        Assign myself
                      </button>
                    )}
                  {!showAssigneePicker ? (
                    <button
                      type="button"
                      onClick={() => setShowAssigneePicker(true)}
                      className="glass-button-sm w-full"
                    >
                      <Plus className="w-3 h-3" />
                      Add assignee
                    </button>
                  ) : (
                    <div className="subcard p-2 space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-glass-300">Search players</span>
                        <button
                          type="button"
                          onClick={() => {
                            setShowAssigneePicker(false);
                            setAssigneeSearchQuery('');
                          }}
                          className="btn-icon btn-icon-sm"
                          aria-label="Close assignee search"
                          title="Close"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-glass-500 pointer-events-none" />
                        <input
                          className="glass-input text-xs w-full pl-8 pr-8"
                          placeholder="Username or UUID…"
                          value={assigneeSearchQuery}
                          onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                          autoComplete="off"
                        />
                        {assigneeSearchLoading && (
                          <Loader className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-glass-400" />
                        )}
                      </div>
                      {assigneeSearchQuery.length > 0 && assigneeSearchQuery.length < 2 && (
                        <p className="text-[11px] text-glass-500 px-0.5">Type at least 2 characters…</p>
                      )}
                      {assigneeSearchError && (
                        <p className="text-[11px] text-red-400 px-0.5">{assigneeSearchError}</p>
                      )}
                      {debouncedAssigneeSearch.length >= 2 &&
                        !assigneeSearchLoading &&
                        !assigneeSearchError &&
                        assigneeSearchRows.length === 0 && (
                          <p className="text-[11px] text-glass-500 px-0.5">No players found</p>
                        )}
                      {assigneeSearchRows.length > 0 && (
                        <ul className="subcard max-h-44 overflow-y-auto divide-y divide-glass-200/10">
                          {assigneeSearchRows.map((row) => {
                            const already = assigneeUuidSet.has(row.uuid);
                            return (
                              <li key={row.uuid}>
                                <button
                                  type="button"
                                  disabled={addAssigneeBusy || already}
                                  onClick={() => void handleAddAssigneeByUuid(row.uuid)}
                                  className={cn(
                                    'w-full flex items-center justify-between gap-2 px-2 py-2 text-left text-xs hover:bg-glass-200/10 transition-colors',
                                    already && 'opacity-50 cursor-not-allowed',
                                  )}
                                >
                                  <PlayerProfile uuid={row.uuid} name={row.name} inline showUuid={false} />
                                  {already ? (
                                    <span className="text-[10px] text-glass-500 shrink-0">Added</span>
                                  ) : (
                                    <Plus className="w-3 h-3 text-accent-400 shrink-0" />
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Labels — elevate while dropdown is open so the menu stacks above following sections */}
        <div className={cn('glass-card p-4 space-y-2', showLabelDropdown && 'relative z-50')}>
          {renderSectionHeader('labels', <Tag className="w-3.5 h-3.5" />, 'Labels', card.labels.length)}
          {expandedSections.labels && (
            <div className="pt-2 space-y-2">
              {card.labels.length === 0 ? (
                <p className="text-sm text-glass-500">No labels</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {card.labels.map(l => (
                    <span
                      key={l.id}
                      className="badge border"
                      style={{
                        backgroundColor: `${l.color}20`,
                        borderColor: `${l.color}40`,
                        color: l.color,
                      }}
                    >
                      {l.name}
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => void handleRemoveLabel(l.id)}
                          disabled={removeLabelBusy === l.id}
                          className="hover:opacity-75"
                          aria-label={`Remove label ${l.name}`}
                          title="Remove label"
                        >
                          {removeLabelBusy === l.id ? <Loader className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
              {canWrite && availableLabels.length > 0 && (
                <div className="relative" ref={labelDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowLabelDropdown(prev => !prev)}
                    className="glass-button-sm"
                    disabled={addLabelBusy}
                  >
                    {addLabelBusy ? <Loader className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Add Label
                  </button>
                  {showLabelDropdown && (
                    <div className="absolute z-[60] mt-1 left-0 w-56 max-h-48 overflow-y-auto bg-[var(--bg-elevated)] border border-glass-200/15 rounded-lg shadow-[var(--shadow-popover)] p-1">
                      {availableLabels.map(l => (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => void handleAddLabel(l.id)}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-glass-100/10 rounded flex items-center gap-2"
                        >
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: l.color }} />
                          <span className="text-glass-200">{l.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Checklists */}
        <div className="glass-card p-4 space-y-3">
          {renderSectionHeader('checklists', <CheckSquare className="w-3.5 h-3.5" />, 'Checklists', card.checklists.length)}
          {expandedSections.checklists && (
            <div className="pt-2 space-y-4">
              {card.checklists.length === 0 && !canWrite && (
                <p className="text-sm text-glass-500">No checklists</p>
              )}
              {card.checklists.map((cl, clIdx) => {
                const total = cl.items.length;
                const checked = cl.items.filter(i => i.is_checked === 1).length;
                const pct = total > 0 ? Math.round((checked / total) * 100) : 0;

                return (
                  <div key={cl.id} className="subcard p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      {editChecklistTitleId === cl.id ? (
                        <div className="flex items-center gap-1 flex-1">
                          <input
                            className="glass-input text-sm flex-1"
                            value={editChecklistTitle}
                            onChange={e => setEditChecklistTitle(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') void handleUpdateChecklistTitle(cl.id);
                              if (e.key === 'Escape') setEditChecklistTitleId(null);
                            }}
                            autoFocus
                          />
                          <button type="button" onClick={() => void handleUpdateChecklistTitle(cl.id)} disabled={editChecklistTitleBusy} className="btn-icon btn-icon-sm text-green-400 hover:text-green-300" aria-label="Save checklist title" title="Save">
                            {editChecklistTitleBusy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                          </button>
                          <button type="button" onClick={() => setEditChecklistTitleId(null)} className="btn-icon btn-icon-sm" aria-label="Cancel" title="Cancel">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="text-sm font-semibold text-white hover:text-accent-300 transition-colors text-left"
                          onClick={() => {
                            if (canWrite) {
                              setEditChecklistTitleId(cl.id);
                              setEditChecklistTitle(cl.title);
                            }
                          }}
                        >
                          {cl.title}
                        </button>
                      )}
                      <div className="flex items-center gap-1">
                        {canWrite && clIdx > 0 && (
                          <button
                            type="button"
                            onClick={() => void handleMoveChecklist(cl.id, clIdx - 1)}
                            disabled={moveChecklistBusy === cl.id}
                            className="btn-icon btn-icon-sm"
                            title="Move up"
                            aria-label="Move checklist up"
                          >
                            <GripVertical className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            onClick={() => setShowDeleteChecklistId(cl.id)}
                            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                            aria-label="Delete checklist"
                            title="Delete checklist"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Progress bar */}
                    {total > 0 && (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[var(--surface-inset)] rounded-full overflow-hidden">
                          <div
                            className={cn('h-full rounded-full transition-all', pct === 100 ? 'bg-green-500' : 'bg-accent-500')}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-xs text-glass-500 tabular-nums">{checked}/{total}</span>
                      </div>
                    )}

                    {/* Items */}
                    <ul className="space-y-1.5">
                      {cl.items.map((item, itemIdx) => (
                        <li key={item.id} className="flex items-start gap-2 text-sm group/item">
                          <input
                            type="checkbox"
                            className="form-checkbox mt-0.5"
                            checked={item.is_checked === 1}
                            disabled={!canWrite || checklistBusyId === item.id}
                            onChange={() =>
                              void handleToggleChecklistItem(item.id, item.is_checked !== 1)
                            }
                          />
                          {editChecklistItemId === item.id ? (
                            <div className="flex items-center gap-1 flex-1">
                              <input
                                className="glass-input text-xs flex-1"
                                value={editChecklistItemContent}
                                onChange={e => setEditChecklistItemContent(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') void handleUpdateChecklistItem(item.id);
                                  if (e.key === 'Escape') setEditChecklistItemId(null);
                                }}
                                autoFocus
                              />
                              <button type="button" onClick={() => void handleUpdateChecklistItem(item.id)} disabled={editChecklistItemBusy} className="btn-icon btn-icon-sm text-green-400 hover:text-green-300" aria-label="Save item" title="Save">
                                {editChecklistItemBusy ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                              </button>
                              <button type="button" onClick={() => setEditChecklistItemId(null)} className="btn-icon btn-icon-sm" aria-label="Cancel" title="Cancel">
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <span
                              className={cn(
                                'text-glass-200 flex-1 cursor-pointer hover:text-accent-300 transition-colors',
                                item.is_checked === 1 && 'line-through text-glass-500',
                              )}
                              onClick={() => {
                                if (canWrite) {
                                  setEditChecklistItemId(item.id);
                                  setEditChecklistItemContent(item.content);
                                }
                              }}
                            >
                              {item.content}
                            </span>
                          )}
                          {item.assigned_to && (
                            <PlayerProfile uuid={item.assigned_to} inline showUuid={false} showAvatar />
                          )}
                          {item.due_date && (
                            <span className="text-xs text-glass-500 flex-shrink-0">{safeFormat(item.due_date, 'MMM d')}</span>
                          )}
                          {checklistBusyId === item.id && <Loader className="w-3.5 h-3.5 animate-spin text-accent-300 flex-shrink-0" />}
                          {canWrite && (
                            <div className="opacity-0 group-hover/item:opacity-100 flex items-center gap-0.5 flex-shrink-0 transition-opacity">
                              {itemIdx > 0 && (
                                <button
                                  type="button"
                                  onClick={() => void handleMoveChecklistItem(item.id, itemIdx - 1)}
                                  disabled={moveChecklistItemBusy === item.id}
                                  className="btn-icon btn-icon-sm"
                                  title="Move up"
                                  aria-label="Move item up"
                                >
                                  <GripVertical className="w-3 h-3" />
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setShowDeleteChecklistItemId(item.id)}
                                className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                                aria-label="Delete item"
                                title="Delete item"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>

                    {/* Add item */}
                    {canWrite && (
                      <div className="flex gap-2 pt-1">
                        <input
                          className="glass-input text-xs flex-1"
                          placeholder="Add item…"
                          value={newItemContent[cl.id] ?? ''}
                          onChange={e => setNewItemContent(prev => ({ ...prev, [cl.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') void handleCreateChecklistItem(cl.id); }}
                        />
                        <button
                          type="button"
                          onClick={() => void handleCreateChecklistItem(cl.id)}
                          disabled={checklistBusyId === `new-${cl.id}` || !newItemContent[cl.id]?.trim()}
                          className="glass-button-sm"
                        >
                          {checklistBusyId === `new-${cl.id}` ? <Loader className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add checklist */}
              {canWrite && (
                <div className="flex gap-2">
                  <input
                    className="glass-input text-xs flex-1"
                    placeholder="New checklist name…"
                    value={newChecklistTitle}
                    onChange={e => setNewChecklistTitle(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') void handleCreateChecklist(); }}
                  />
                  <button
                    type="button"
                    onClick={() => void handleCreateChecklist()}
                    disabled={newChecklistBusy}
                    className="glass-button-sm"
                  >
                    {newChecklistBusy ? <Loader className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                    Add Checklist
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="glass-card p-4 space-y-3">
          {renderSectionHeader('comments', <MessageSquare className="w-3.5 h-3.5" />, 'Comments', card.comments.length)}
          {expandedSections.comments && (
            <div className="pt-2 space-y-3">
              {card.comments.length === 0 && (
                <p className="text-sm text-glass-500">No comments yet</p>
              )}
              <ul className="divide-y divide-glass-200/10 max-h-80 overflow-y-auto pr-1">
                {card.comments.map(c => (
                  <li key={c.id} className="py-3 first:pt-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <PlayerProfile uuid={c.author_id} inline showUuid={false} />
                        <p className="text-xs text-glass-500 mt-0.5 tabular-nums">
                          {safeFormatDistanceToNow(c.created_at, { addSuffix: true })}
                          {c.updated_at && <span className="ml-1">(edited)</span>}
                        </p>
                        {editingCommentId === c.id ? (
                          <div className="mt-2 space-y-2">
                            <textarea
                              className="glass-input w-full text-sm min-h-[60px] resize-y"
                              value={editCommentContent}
                              onChange={e => setEditCommentContent(e.target.value)}
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() => void handleUpdateComment(c.id)}
                                disabled={editCommentBusy}
                                className="glass-button-sm"
                              >
                                {editCommentBusy ? <Loader className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                Save
                              </button>
                              <button type="button" onClick={() => setEditingCommentId(null)} className="glass-button-secondary btn-xs">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2">
                            <MarkdownRenderer content={c.content} className="text-sm" />
                          </div>
                        )}
                      </div>
                      {canWrite && editingCommentId !== c.id && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => { setEditingCommentId(c.id); setEditCommentContent(c.content); }}
                            className="btn-icon btn-icon-sm"
                            aria-label="Edit comment"
                            title="Edit comment"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowDeleteCommentId(c.id)}
                            className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                            aria-label="Delete comment"
                            title="Delete comment"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {canWrite && (
                <div className="space-y-2 pt-3 border-t border-glass-200/10">
                  <textarea
                    className="glass-input w-full text-sm min-h-[80px] resize-y"
                    placeholder="Write a comment… (Markdown supported)"
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => void handleCreateComment()}
                    disabled={commentSubmitting || !newComment.trim()}
                    className="glass-button-sm"
                  >
                    {commentSubmitting && <Loader className="w-3.5 h-3.5 animate-spin" />}
                    Post comment
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="glass-card p-4 space-y-3">
          {renderSectionHeader('attachments', <Paperclip className="w-3.5 h-3.5" />, 'Attachments', card.attachments.length)}
          {expandedSections.attachments && (
            <div className="pt-2 space-y-2">
              {card.attachments.length === 0 && (
                <p className="text-sm text-glass-500">No attachments</p>
              )}
              <ul className="space-y-2">
                {card.attachments.map(att => {
                  const previewUrl = attachmentPreviewUrls[att.id];
                  const showImg = isImageContentType(att.content_type) && previewUrl;
                  return (
                    <li key={att.id} className="subcard overflow-hidden">
                      {showImg ? (
                        <div className="bg-glass-900/40 border-b border-glass-200/10">
                          <img
                            src={previewUrl}
                            alt={att.filename}
                            className="w-full max-h-56 object-contain bg-black/30"
                            loading="lazy"
                          />
                        </div>
                      ) : null}
                      <div className="flex items-center justify-between gap-2 px-3 py-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-glass-200 truncate">{att.filename}</p>
                          <p className="text-xs text-glass-500 tabular-nums">
                            {formatFileSize(att.size)} · {att.content_type} ·{' '}
                            <PlayerProfile uuid={att.uploader_id} inline showUuid={false} showAvatar={false} />
                          </p>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => void handleOpenAttachmentInNewTab(att.id)}
                            className="btn-icon btn-icon-sm"
                            title="Open in new tab"
                            aria-label="Open in new tab"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDownloadAttachment(att.id, att.filename)}
                            disabled={downloadingAttId === att.id}
                            className="btn-icon btn-icon-sm text-accent-400 hover:text-accent-300"
                            title="Download"
                            aria-label="Download"
                          >
                            {downloadingAttId === att.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                          </button>
                          {canWrite && (
                            <button
                              type="button"
                              onClick={() => setShowDeleteAttachmentId(att.id)}
                              className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                              aria-label="Delete attachment"
                              title="Delete attachment"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>

              {canWrite && (
                <div className="flex items-center gap-2 pt-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={e => void handleUploadAttachments(e.target.files)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFiles}
                    className="glass-button-sm"
                  >
                    {uploadingFiles ? <Loader className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                    Upload files
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Links */}
        <div className="glass-card p-4 space-y-3">
          {renderSectionHeader('links', <Link2 className="w-3.5 h-3.5" />, 'Links', card.links.length)}
          {expandedSections.links && (
            <div className="pt-2 space-y-2">
              {card.links.length === 0 && (
                <p className="text-sm text-glass-500">No links</p>
              )}
              <ul className="space-y-2">
                {card.links.map(lnk => {
                  const cfg = LINK_TYPE_CONFIG[lnk.link_type];
                  const preview = linkPreviewLabels[lnk.id];
                  const routeTo =
                    lnk.link_type === 'kanban_card' && cfg?.route && boardId
                      ? `${cfg.route}/${encodeURIComponent(boardId)}?card=${encodeURIComponent(lnk.link_target_id)}`
                      : cfg?.route
                        ? `${cfg.route}/${encodeURIComponent(lnk.link_target_id)}`
                        : null;
                  const linkPrimary =
                    preview ||
                    (lnk.link_type === 'note' || lnk.link_type === 'warning'
                      ? `Open ${cfg?.label ?? lnk.link_type}`
                      : lnk.link_target_id);
                  return (
                    <li key={lnk.id} className="subcard flex items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="chip">
                            {cfg?.label ?? lnk.link_type}
                          </span>
                          {lnk.link_type === 'player' ? (
                            <PlayerProfile uuid={lnk.link_target_id} inline showUuid={false} />
                          ) : lnk.link_type === 'ticket' ? (
                            <TicketIdLink ticketId={lnk.link_target_id} className="text-sm text-glass-200 hover:text-accent-300 font-mono">
                              #{lnk.link_target_id}
                            </TicketIdLink>
                          ) : routeTo ? (
                            <Link to={routeTo} className="text-sm text-accent-300 hover:text-accent-200 hover:underline underline-offset-2 min-w-0">
                              <span className="block truncate font-medium">{linkPrimary}</span>
                              {(preview || lnk.link_type === 'kanban_card') && (
                                <span className="block text-[10px] font-mono text-glass-500 truncate mt-0.5">
                                  {lnk.link_target_id}
                                </span>
                              )}
                            </Link>
                          ) : (
                            <div className="min-w-0 flex-1">
                              <span className="text-sm text-glass-200 block truncate">{linkPrimary}</span>
                              <span className="text-[10px] font-mono text-glass-500 truncate block mt-0.5">
                                {lnk.link_target_id}
                              </span>
                            </div>
                          )}
                        </div>
                        {lnk.notes && <p className="text-xs text-glass-500 mt-1">{lnk.notes}</p>}
                      </div>
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => setShowDeleteLinkId(lnk.id)}
                          className="btn-icon btn-icon-sm text-red-400 hover:text-red-300 flex-shrink-0"
                          aria-label="Remove link"
                          title="Remove link"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              {canWrite && (
                <button
                  type="button"
                  onClick={() => setShowAddLink(true)}
                  className="glass-button-sm"
                >
                  <Plus className="w-3 h-3" />
                  Add Link
                </button>
              )}
            </div>
          )}
        </div>

        {/* Watchers */}
        <div className="glass-card p-4 space-y-3">
          {renderSectionHeader('watchers', <Eye className="w-3.5 h-3.5" />, 'Watchers', card.watchers.length)}
          {expandedSections.watchers && (
            <div className="pt-2 space-y-2">
              {card.watchers.length === 0 && (
                <p className="text-sm text-glass-500">No watchers</p>
              )}
              <ul className="space-y-2">
                {card.watchers.map(w => (
                  <li key={w.user_uuid} className="flex items-center gap-2">
                    <PlayerProfile uuid={w.user_uuid} inline showUuid={false} />
                    <span className="text-xs text-glass-500">
                      since {safeFormat(w.watched_at, 'PPp')}
                    </span>
                  </li>
                ))}
              </ul>
              {currentUserUuid && (
                <button
                  type="button"
                  onClick={() => void handleToggleWatch()}
                  disabled={watchBusy}
                  className="glass-button-sm"
                >
                  {watchBusy ? <Loader className="w-3 h-3 animate-spin" /> : isWatching ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {isWatching ? 'Unwatch' : 'Watch'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Tab: Activity ───────────────────────────────────────────────────────
  const renderActivity = () => (
    <div className="space-y-3 animate-in slide-in-from-right-4 duration-200">
      {activityLoading && activity.length === 0 ? (
        <div className="space-y-3">
          <SkeletonLoader className="h-12 w-full" />
          <SkeletonLoader className="h-12 w-full" />
          <SkeletonLoader className="h-12 w-full" />
        </div>
      ) : activity.length === 0 ? (
        <p className="text-sm text-glass-500 text-center py-8">No activity recorded</p>
      ) : (
        <>
          <ul className="space-y-3">
            {activity.map(entry => (
              <li key={entry.id} className="flex gap-3 border-l-2 border-accent-500/20 pl-3 py-1">
                <div className="flex-shrink-0 mt-0.5">
                  <Activity className="w-4 h-4 text-accent-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-glass-200">{entry.description}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <PlayerProfile uuid={entry.actor_id} inline showUuid={false} showAvatar={false} />
                    <span className="text-xs text-glass-500 tabular-nums">
                      {safeFormatDistanceToNow(entry.created_at, { addSuffix: true })}
                    </span>
                  </div>
                  {(entry.old_value || entry.new_value) && (
                    <div className="mt-1 text-xs text-glass-500">
                      {entry.old_value && <span className="line-through mr-2">{entry.old_value}</span>}
                      {entry.new_value && <span className="text-glass-300">{entry.new_value}</span>}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {activity.length < activityTotal && (
            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => void loadActivity(activityPage + 1)}
                disabled={activityLoading}
                className="glass-button-sm"
              >
                {activityLoading ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ── Tab: History ────────────────────────────────────────────────────────
  const renderHistory = () => (
    <div className="space-y-3 animate-in slide-in-from-right-4 duration-200">
      {historyLoading ? (
        <div className="space-y-3">
          <SkeletonLoader className="h-12 w-full" />
          <SkeletonLoader className="h-12 w-full" />
          <SkeletonLoader className="h-12 w-full" />
        </div>
      ) : history.length === 0 ? (
        <p className="text-sm text-glass-500 text-center py-8">No moves recorded</p>
      ) : (
        <ul className="space-y-4 relative">
          <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-accent-500/20" />
          {history.map(h => (
            <li key={h.id} className="flex gap-3 relative">
              <div className="w-4 h-4 rounded-full bg-accent-500/30 border-2 border-accent-500 flex-shrink-0 mt-0.5 z-10" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-glass-200">
                  {h.from_list_id ? (
                    <>
                      <span className="text-glass-400">{listTitleById[h.from_list_id] ?? 'Unknown'}</span>
                      <span className="mx-1.5 text-glass-500">→</span>
                      <span className="text-white font-medium">{listTitleById[h.to_list_id] ?? 'Unknown'}</span>
                    </>
                  ) : (
                    <>Entered <span className="text-white font-medium">{listTitleById[h.to_list_id] ?? 'list'}</span></>
                  )}
                </p>
                {(h.from_swimlane_id || h.to_swimlane_id) && (
                  <p className="text-xs text-glass-500 mt-0.5">
                    Swimlane: {h.from_swimlane_id ? swimlaneTitleById[h.from_swimlane_id] ?? 'Unknown' : 'None'}
                    {' → '}
                    {h.to_swimlane_id ? swimlaneTitleById[h.to_swimlane_id] ?? 'Unknown' : 'None'}
                  </p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <PlayerProfile uuid={h.moved_by} inline showUuid={false} showAvatar={false} />
                  <span className="text-xs text-glass-500 tabular-nums">
                    {safeFormatDistanceToNow(h.moved_at, { addSuffix: true })}
                  </span>
                  <span className="text-xs text-glass-500 tabular-nums">{safeFormat(h.moved_at, 'PPp')}</span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  // ── Main render ─────────────────────────────────────────────────────────
  const panelContent = (
    <div className="fixed inset-0 z-[9999] flex">
      {/* Backdrop */}
      <div className="flex-1 bg-[var(--bg-overlay)] backdrop-blur-sm" onClick={onClose} aria-hidden />

      {/* Panel */}
      <div
        className={cn(
          'w-full border-l border-glass-200/10 overflow-y-auto shadow-2xl shadow-black/40',
          'bg-[var(--bg-elevated)] backdrop-blur-xl',
          isMobile ? 'max-w-full' : 'max-w-2xl',
        )}
      >
        {loading ? (
          <div className="p-4 md:p-6 space-y-4" aria-busy="true">
            <div className="flex items-center justify-between gap-3">
              <SkeletonLoader className="h-6 w-2/3" />
              <button type="button" onClick={onClose} className="btn-icon" aria-label="Close" title="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-2">
              <SkeletonLoader className="h-5 w-16 rounded-full" />
              <SkeletonLoader className="h-5 w-10 rounded-full" />
            </div>
            <div className="flex gap-2">
              <SkeletonLoader className="h-7 w-20" />
              <SkeletonLoader className="h-7 w-20" />
              <SkeletonLoader className="h-7 w-20" />
            </div>
            <SkeletonLoader className="h-8 w-full" />
            {[1, 2, 3].map((i) => (
              <div key={i} className="glass-card p-4 space-y-3">
                <SkeletonLoader className="h-4 w-32" />
                <SkeletonLoader className="h-3 w-full" />
                <SkeletonLoader className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        ) : !card ? (
          <EmptyState
            bare
            compact
            tone="danger"
            icon={AlertTriangle}
            title="Card not found."
            description="It may have been deleted or moved to another board."
            action={<button type="button" onClick={onClose} className="glass-button">Close</button>}
          />
        ) : (
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex-shrink-0 p-4 md:p-6 border-b border-glass-200/10 space-y-3">
              {/* Title row */}
              <div className="flex items-start justify-between gap-3">
                {editingField === 'title' ? (
                  <input
                    className="glass-input text-lg font-semibold flex-1"
                    value={editValues.title ?? ''}
                    onChange={e => setEditValues(prev => ({ ...prev, title: e.target.value }))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') void handleSaveField('title');
                      if (e.key === 'Escape') cancelEditing();
                    }}
                    onBlur={() => void handleSaveField('title')}
                    autoFocus
                  />
                ) : (
                  <h2
                    className={cn(
                      'text-lg font-semibold tracking-tight text-white truncate',
                      canWrite && 'cursor-pointer hover:text-accent-300 transition-colors',
                    )}
                    onClick={() => canWrite && startEditing('title', card.title)}
                  >
                    {card.title}
                  </h2>
                )}
                <button type="button" onClick={onClose} className="btn-icon flex-shrink-0" aria-label="Close" title="Close (Esc)">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Card meta badges */}
              <div className="flex flex-wrap items-center gap-2">
                <KanbanCardTypeBadge type={card.card_type} />
                <KanbanPriorityBadge priority={card.priority} />
                <KanbanClassOfServiceIndicator classOfService={card.class_of_service} showLabel />
                {card.is_blocked === 1 && (
                  <span className="badge badge-danger">
                    <Ban className="w-3 h-3" /> Blocked
                  </span>
                )}
                {card.is_archived === 1 && (
                  <span className="badge badge-warning">
                    <Archive className="w-3 h-3" /> Archived
                  </span>
                )}
              </div>

              {/* Action buttons */}
              {canWrite && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setShowArchiveConfirm(true)}
                    disabled={archiveBusy}
                    className="glass-button-sm"
                  >
                    <Archive className="w-3.5 h-3.5" />
                    {card.is_archived === 1 ? 'Unarchive' : 'Archive'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    className="btn-danger btn-xs"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                  {card.is_blocked === 1 ? (
                    <button
                      type="button"
                      onClick={() => void handleBlockCard(false)}
                      disabled={blockBusy}
                      className="glass-button-sm text-green-400"
                    >
                      {blockBusy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                      Unblock
                    </button>
                  ) : showBlockInput ? (
                    <div className="flex items-center gap-1">
                      <input
                        className="glass-input text-xs !min-h-0 py-1 px-2 w-36"
                        placeholder="Block reason…"
                        value={blockReasonInput}
                        onChange={e => setBlockReasonInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') void handleBlockCard(true, blockReasonInput); if (e.key === 'Escape') setShowBlockInput(false); }}
                        autoFocus
                      />
                      <button type="button" onClick={() => void handleBlockCard(true, blockReasonInput)} disabled={blockBusy} className="glass-button-sm text-red-400">
                        {blockBusy ? <Loader className="w-3 h-3 animate-spin" /> : 'Block'}
                      </button>
                      <button type="button" onClick={() => setShowBlockInput(false)} className="btn-icon btn-icon-sm" aria-label="Cancel block" title="Cancel">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowBlockInput(true)}
                      className="glass-button-sm text-orange-400"
                    >
                      <Ban className="w-3.5 h-3.5" />
                      Block
                    </button>
                  )}
                  {currentUserUuid && (
                    <button
                      type="button"
                      onClick={() => void handleToggleWatch()}
                      disabled={watchBusy}
                      className="glass-button-sm"
                    >
                      {watchBusy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : isWatching ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      {isWatching ? 'Unwatch' : 'Watch'}
                    </button>
                  )}
                </div>
              )}

              {/* Tabs */}
              <div className="tabs -mx-4 md:-mx-6 px-4 md:px-6" role="tablist">
                {(['details', 'activity', 'history'] as ActiveTab[]).map(tab => (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    onClick={() => setActiveTab(tab)}
                    className="tab capitalize"
                    data-active={activeTab === tab ? 'true' : 'false'}
                    aria-selected={activeTab === tab}
                  >
                    {tab === 'details' && <Edit3 className="w-3.5 h-3.5" />}
                    {tab === 'activity' && <Activity className="w-3.5 h-3.5" />}
                    {tab === 'history' && <History className="w-3.5 h-3.5" />}
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {activeTab === 'details' && renderDetails()}
              {activeTab === 'activity' && renderActivity()}
              {activeTab === 'history' && renderHistory()}
            </div>
          </div>
        )}
      </div>

      {/* Confirmation modals */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => void handleDeleteCard()}
        title="Delete Card"
        message="This card and all its data (comments, checklists, attachments, links) will be permanently deleted. This cannot be undone."
        confirmText="Delete"
        isLoading={deleteBusy}
      />

      <ConfirmationModal
        isOpen={showArchiveConfirm}
        onClose={() => setShowArchiveConfirm(false)}
        onConfirm={() => void handleArchiveCard()}
        title={card?.is_archived === 1 ? 'Unarchive Card' : 'Archive Card'}
        message={card?.is_archived === 1 ? 'This card will be restored to the board.' : 'This card will be archived and hidden from the board.'}
        confirmText={card?.is_archived === 1 ? 'Unarchive' : 'Archive'}
        isLoading={archiveBusy}
      />

      <ConfirmationModal
        isOpen={!!showDeleteCommentId}
        onClose={() => setShowDeleteCommentId(null)}
        onConfirm={() => showDeleteCommentId && void handleDeleteComment(showDeleteCommentId)}
        title="Delete Comment"
        message="This comment will be permanently removed."
        confirmText="Delete"
        isLoading={deleteItemBusy}
      />

      <ConfirmationModal
        isOpen={!!showDeleteAttachmentId}
        onClose={() => setShowDeleteAttachmentId(null)}
        onConfirm={() => showDeleteAttachmentId && void handleDeleteAttachment(showDeleteAttachmentId)}
        title="Delete Attachment"
        message="This attachment will be permanently deleted from storage."
        confirmText="Delete"
        isLoading={deleteItemBusy}
      />

      <ConfirmationModal
        isOpen={!!showDeleteChecklistId}
        onClose={() => setShowDeleteChecklistId(null)}
        onConfirm={() => showDeleteChecklistId && void handleDeleteChecklist(showDeleteChecklistId)}
        title="Delete Checklist"
        message="This checklist and all its items will be permanently deleted."
        confirmText="Delete"
        isLoading={deleteItemBusy}
      />

      <ConfirmationModal
        isOpen={!!showDeleteChecklistItemId}
        onClose={() => setShowDeleteChecklistItemId(null)}
        onConfirm={() => showDeleteChecklistItemId && void handleDeleteChecklistItem(showDeleteChecklistItemId)}
        title="Delete Checklist Item"
        message="This checklist item will be removed."
        confirmText="Delete"
        isLoading={deleteItemBusy}
      />

      <ConfirmationModal
        isOpen={!!showDeleteLinkId}
        onClose={() => setShowDeleteLinkId(null)}
        onConfirm={() => showDeleteLinkId && void handleDeleteLink(showDeleteLinkId)}
        title="Delete Link"
        message="This link will be permanently removed from the card."
        confirmText="Delete"
        isLoading={deleteItemBusy}
      />

      {showAddLink && card && (
        <AddCardLinkModal
          isOpen={showAddLink}
          onClose={() => setShowAddLink(false)}
          cardId={card.id}
          boardId={boardId}
          onCreated={() => {
            setShowAddLink(false);
            void afterMutation();
          }}
        />
      )}
    </div>
  );

  return createPortal(panelContent, document.body);
};

